/*
 * Copyright (C) Ivo Ladage-van Doorn - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Ivo Ladage-van Doorn <ivol37@gmail.com>, 2024
 */
import { GenealogySource, Parent } from "../Genealogy/types";

let sources: GenealogySource[];

const getSource = (id: string): GenealogySource[] => {
  return sources.filter((s) => s.id === id);
};

const getSources = (ids: string[]): GenealogySource[] => {
  return sources.filter((source) => ids.filter((id) => source.id === id).length > 0);
};

export const getHuygensParents = (genSources: GenealogySource[]): Parent[] => {
  sources = genSources;

  const parents = [];

  /**
   * Vraag is wanneer Jan Huijgens geboren is. Vermoedelijk rond 1630 en vermoedelijk in Breda. Vermoedelijke Nederlands Hervormd / (voorheen Nederduits-gereformeerd).
   *
   * Mogelijke schrijfwijzen:
   *
   * Huigens
   * Huygens
   * Huijgens
   * Hügens
   *
   * Gechecked:
   * https://www.wiewaswie.nl X
   * https://noord-hollandsarchief.nl/
   * https://www.familysearch.org
   * https://www.openarch.nl
   *
   * Jenneken Huygens is wel wat meer over te vinden, vertrok naar Nijmegen.
   */

  // Generation 8
  parents.push({
    name: "Jan Huijgens",
    backgroundInfo: {
      text: "Geboren ergens tussen 1600 en 1638, vermoedelijk rond 1630. Woonde tijdens huwelijk in de Hooge Baracken te Breda, en dat huwelijk was voor 1654. Jan was soldaat onder kapitein Ingolbij volgens de huwelijksakte. Maar waarschijnlijk is dit George Ingoldesbij, aangesteld in 1648 (zie <a href='https://www.nationaalarchief.nl/onderzoeken/index/nt00194/2eac500a-ae58-102d-8e23-0050569c51dd'> het nationaal archief</a>).",
    },
    mariages: [
      {
        date: "05-06-1651",
        city: "Breda",
        sources: getSource("1651TBHA"),
        partner: {
          name: "Grietjen Veniges / Tijssen",
        },
        children: [
          {
            name: "Jenneken Huygens",
            birth: {
              date: "14-04-1652",
              city: "Breda",
              sources: getSource("1652SBGA"),
            },
          },
          {
            name: "Berendt Huijgens",
            mainAncestor: true,
            birth: {
              date: "20-01-1654",
              city: "Breda",
              sources: getSource("1654SBGA"),
            },
            baptism: {
              date: "25-01-1654",
              city: "Breda",
              sources: getSource("1654SBGA"),
            },
          },
        ],
      },
      {
        partner: {
          name: "Margriet Huygens",
        },
        children: [
          {
            name: "Johannes Huygens",
            birth: {
              date: "12-11-1655",
              city: "Breda",
              sources: getSource("1655DBGA"),
            },
          },
        ],
      },
    ],
  });

  // Generation 7
  parents.push({
    name: "Berendt Huijgens",
    backgroundInfo: {
      text: "Bij zijn huwelijk op 3 maart 1680 was Berendt 'Sergeant van kapitein Brant van Delen'.",
    },
    birth: {
      date: "20-01-1654",
      city: "Breda",
      sources: getSource("1654SBGA"),
    },
    baptism: {
      date: "25-01-1654",
      city: "Breda",
      sources: getSource("1654SBGA"),
    },
    mariages: [
      {
        date: "03-03-1680",
        city: "Zutphen",
        sources: getSource("1680TZHA"),
        partner: {
          name: "Elisabeth Tiemens",
        },
        children: [
          {
            name: "Johanna Huygens",
            birth: {
              date: "13-01-1681",
              city: "Breda",
              sources: getSource("1681SBGA"),
            },
          },
          {
            name: "Henrica Huygens",
            birth: {
              date: "19-11-1682",
              city: "Zutphen",
              sources: getSource("1682DZGA"),
            },
            baptism: {
              date: "19-11-1682",
              city: "Zutphen",
              sources: getSource("1682DZGA"),
            },
          },
          {
            name: "Gerrit Huygens",
            birth: {
              date: "14-03-1684",
              city: "Zutphen",
              sources: getSource("1684DZGA"),
            },
            baptism: {
              date: "14-03-1684",
              city: "Zutphen",
              sources: getSource("1684DZGA"),
            },
          },
          {
            name: "Margriet Huygens",
            birth: {
              date: "11-08-1686",
              city: "Zutphen",
              sources: getSource("1686DZGA"),
            },
            baptism: {
              date: "11-08-1686",
              city: "Zutphen",
              sources: getSource("1686DZGA"),
            },
          },
          {
            name: "Jan Huygens",
            birth: {
              date: "12-07-1693",
              city: "Zutphen",
              sources: getSource("1693DZGA"),
            },
            baptism: {
              date: "12-07-1693",
              city: "Zutphen",
              sources: getSource("1693DZGA"),
            },
          },
          {
            name: "Bernardina Huygens",
            birth: {
              date: "26-04-1696",
              city: "Zutphen",
              sources: getSource("1696DZGA"),
            },
            baptism: {
              date: "26-04-1696",
              city: "Zutphen",
              sources: getSource("1696DZGA"),
            },
          },
          {
            name: "Gerrit/Gerard Huijgens",
            mainAncestor: true,
            birth: {
              date: "10-08-1701",
              city: "Zutphen",
              sources: getSource("1701DZGA"),
            },
            death: {
              city: "Haarlem",
            },
          },
        ],
      },
    ],
  });

  // Generation 6
  parents.push({
    name: "Gerard/Gerrit Huijgens",
    backgroundInfo: {
      text: "Gerrit of Gerard werd geboren in Zutphen. Vreemd genoeg wordt hij in zijn doop -en huwelijksakte 'Gerard' genoemd, maar bij iedere doop van zijn kinderen 'Gerrit'. Vermoedelijk is hij zichzelf later Gerrit gaan noemen. Hij was lid van de Nederlands Hervormde gemeente te Haarlem. Hij woonde eerst aan de grote Houtstraat, in het centrum van Haarlem. Bij zijn tweede huwelijk met Schalbroek woonde hij aan de Jans straat.",
    },
    birth: {
      date: "10-08-1701",
      city: "Zutphen",
      sources: getSource("1701DZGA"),
    },
    death: {
      city: "Haarlem",
    },
    mariages: [
      {
        date: "14-03-1732",
        city: "Haarlem",
        sources: getSource("1732HDHA"),
        partner: {
          name: "Geertrui Aleyda van Stegen",
          birth: {
            date: "06-05-1701",
            city: "Zwolle",
          },
          death: {
            date: "10-01-1740",
            city: "Haarlem",
          },
        },
        children: [
          {
            name: "Barent Jan Huigens",
            baptism: {
              date: "14-01-1733",
              city: "Haarlem",
              sources: getSource("1733NAGA"),
            },
          },
          {
            name: "Elisabeth Huygens",
            baptism: {
              date: "20-01-1736",
              city: "Haarlem",
              sources: getSource("1736NAGA"),
            },
          },
          {
            name: "Engbert Huijgens",
            mainAncestor: true,
            baptism: {
              date: "22-04-1738",
              city: "Haarlem",
              sources: getSource("1738DHGA"),
            },
          },
        ],
      },
      {
        date: "28-03-1745",
        city: "Haarlem",
        sources: getSource("1745HDHA"),
        partner: {
          name: "Catharina Schalbroek",
          birth: {
            city: "Amsterdam",
          },
          death: {
            city: "Haarlem",
          },
        },
      },
    ],
  });

  // Generation 6
  parents.push({
    name: "Engbert Huijgens",
    backgroundInfo: {
      text: "De naam Engbert Huijgens duikt een aantal maal op in notariële stukken m.b.t. een Insinuatie. Waarschijnlijk omdat Engbert een aantal maal samen met een zekere Jacob van Dam een aanklacht heeft ingediend tegen iemand anders. Door de tekst in de stukken te transcriberen moet dat te achterhalen zijn.",
      sources: getSources(["1775SAIN", "1783SAIN"]),
    },
    baptism: {
      date: "22-04-1738",
      city: "Haarlem",
      sources: getSource("1738DHGA"),
    },
    mariages: [
      {
        partner: {
          name: "Agneta van Oosterom",
          birth: {
            date: "13-10-1744",
            city: "Haarlem",
            sources: getSource("1821NAOA"),
          },
          death: {
            date: "06-11-1821",
            city: "Haarlem",
            sources: getSource("1821NAOA"),
          },
        },
        children: [
          {
            name: "Gerrit Huijgens",
            birth: {
              date: "02-03-1770",
              city: "Haarlem",
              sources: getSource("1770NAGA"),
            },
            baptism: {
              date: "04-03-1770",
              city: "Haarlem",
              sources: getSource("1770NAGA"),
            },
          },
          {
            name: "Willem Jacob Huijgens",
            mainAncestor: true,
            birth: {
              date: "2-1-1774",
              city: "Haarlem",
              sources: getSource("1774NAGA"),
            },
            death: {
              date: "15-05-1826",
              city: "Haarlem",
              sources: getSource("1826NAOA"),
            },
          },
          {
            name: "Jan Anthoni Huijgens",
            birth: {
              date: "18-11-1776",
              city: "Haarlem",
              sources: getSource("1776NAGA"),
            },
            baptism: {
              date: "20-11-1776",
              city: "Haarlem",
              sources: getSource("1776NAGA"),
            },
          },
          {
            name: "Johanna Geertruij Huijgens",
            birth: {
              date: "07-10-1778",
              city: "Haarlem",
              sources: getSource("1776NAGA"),
            },
            baptism: {
              date: "11-10-1778",
              city: "Haarlem",
              sources: getSource("1776NAGA"),
            },
          },
        ],
      },
    ],
  });

  // Generation 5
  parents.push({
    name: "Willem Jacob Huijgens",
    backgroundInfo: {
      text: "Willem was kunstschilder. In 1882 wordt een zoon Willem Jacob genoemd (geboren in 1814) in het patiëntenregister van Binnengasthuis te Amsterdam, wat tegenwoordig het AMC ziekenhuis is. Daarin staat dat hij koopman was, weduwnaar van Jacoba Zonneveld en geen kinderen had.",
    },
    birth: {
      date: "2-1-1774",
      city: "Haarlem",
      sources: getSource("1774NAGA"),
    },
    death: {
      date: "15-05-1826",
      city: "Haarlem",
      sources: getSource("1826NAOA"),
    },
    mariages: [
      {
        partner: {
          name: "Johanna Maria Frederica Courlois",
        },
        children: [
          {
            name: "Engbert Huijgens",
            mainAncestor: true,
            birth: {
              date: "21-09-1800",
              city: "Haarlem",
              sources: getSource("1800SRVT"),
            },
            baptism: {
              date: "28-09-1800",
              city: "Haarlem",
              sources: getSource("1800SRGA"),
            },
            death: {
              date: "16-05-1862",
              city: "Amsterdam",
            },
          },
          {
            name: "Geertruida Maria Wilhelmina Huijgens",
            birth: {
              date: "15-03-1805",
              city: "Haarlem",
              sources: getSource("1805NAGA"),
            },
            baptism: {
              date: "20-03-1805",
              city: "Haarlem",
              sources: getSource("1805NAGA"),
            },
          },
          {
            name: "Gerrit Jan Anthonie Huijgens",
            birth: {
              date: "14-04-1811",
              city: "Beverwijk",
              sources: getSource("1811NAGA"),
            },
          },
          {
            name: "Willem Jacob Huijgens",
            birth: {
              date: "02-05-1814",
              city: "Beverwijk",
              sources: getSource("1814NAGA"),
            },
          },
          {
            name: "Alexander Huijgens",
            birth: {
              date: "05-1818",
              sources: getSource("1818HGGA"),
            },
            death: {
              date: "30-12-1818",
              city: "Den Haag",
              sources: getSource("1818HGGA"),
            },
          },
        ],
      },
    ],
  });

  // Generation 4
  parents.push({
    name: "Engbert Huijgens",
    backgroundInfo: {
      text: "Engbert was schilder en Nederlands hervormd. In 1837 woonde hij kennelijk in Ommerschans en was 'kolonist'. Ten tijde van de volkstelling rond 1850 woonde hij in Amsterdam maar ging hij verhuizen naar Den Haag. In de volkstelling van 1853 staat dat hij straatmuzikant van beroep was, samen met zijn zonen Petrus en Hendrik. Maar bij het huwelijk van zijn dochter staat zijn beroep opgetekend als 'logementhouder'. Petrus Engbert Antie Huijgens was een boef en heeft 3 maanden gezeten in de gevangenis te Leeuwarden voor valsheid in geschriften. Daarmee heeft hij zijn <a href='https://www.gevangeninglas.nl/home/glasplaten-1201-1500/gn1480-petrus-egbert-antonie-huijgens'>eigen webpagina</a> verdiend.",
    },
    birth: {
      date: "21-09-1800",
      city: "Haarlem",
      sources: getSource("1800SRVT"),
    },
    baptism: {
      date: "28-09-1800",
      city: "Haarlem",
      sources: getSource("1800SRGA"),
    },
    death: {
      date: "16-05-1862",
      city: "Amsterdam",
    },
    mariages: [
      {
        date: "20-04-1825",
        city: "Haarlem",
        sources: getSource("1825NAHA"),
        partner: {
          name: "Johanna Janze Nathan",
          birth: {
            date: "16-03-1801",
            city: "Haarlem",
            sources: getSource("1828NAOA"),
          },
          death: {
            date: "13-06-1828",
            city: "Haarlem",
            sources: getSource("1828NAOA"),
          },
        },
        children: [
          {
            name: "Johanna Maria Frederika Huijgens",
            birth: {
              date: "06-05-1825",
              city: "Haarlem",
              sources: getSource("1825NAGA"),
            },
          },
        ],
      },
      {
        date: "16-12-1829",
        city: "Den Haag",
        sources: getSource("1829HGHA"),
        partner: {
          name: "Johanna Jacoba Margaretha Kroes",
          birth: {
            date: "09-09-1811",
            city: "Rotterdam",
            sources: getSource("1811SRVT"),
          },
        },
        children: [
          {
            name: "Geertruida Hendrina Fredrika Huijgens",
            birth: {
              date: "23-12-1830",
              city: "Rotterdam",
              sources: getSource("1844SRGA"),
            },
          },
          {
            name: "Willem Jacob Huijgens",
            birth: {
              date: "28-11-1832",
              city: "Rotterdam",
              sources: getSource("1832SRGA"),
            },
          },
          {
            name: "Jacob Andries Huijgens",
            birth: {
              date: "29-09-1837",
              city: "Ommerschans",
            },
          },
          {
            name: "Johanna Maria Frederica Huijgens",
            birth: {
              date: "17-09-1839",
              city: "Utrecht",
            },
            death: {
              date: "08-12-1840",
              city: "Utrecht",
            },
          },
          {
            name: "Petrus Engbert Antie Huijgens",
            birth: {
              date: "27-03-1842",
              city: "Den Haag",
              sources: getSource("1853SAVT"),
            },
          },
          {
            name: "Hendrik Rutgerus Egbert Huijgens",
            birth: {
              date: "02-09-1844",
              city: "Den Haag",
              sources: getSource("1844SAGA"),
            },
            mainAncestor: true,
          },
          {
            name: "Frederica Louisa Huijgens",
            birth: {
              date: "27-02-1848",
              city: "Den Haag",
              sources: getSource("1853SAVT"),
            },
          },
          {
            name: "Willen Engbert Huijgens",
            birth: {
              date: "15-03-1851",
              city: "Den Haag",
              sources: getSource("1853SAVT"),
            },
          },
        ],
      },
    ],
  });

  // Generation 3
  parents.push({
    name: "Hendrik Rutgerus Egbert Huijgens",
    backgroundInfo: {
      text: "Hendrik was in militaire dienst in 1864. In 1870 was hij spoorwegbeambte.",
    },
    birth: {
      date: "02-09-1844",
      city: "Den Haag",
      sources: getSource("1844SAGA"),
    },
    mariages: [
      {
        partner: {
          name: "Christina van Smaalen",
          birth: {
            date: "11-11-1849",
            city: "Rotterdam",
            sources: getSource("1849SRGA"),
          },
        },
        children: [
          {
            name: "Johanna Jacoba Margaretha Huijgens",
            birth: {
              date: "19-04-1870",
              city: "Zevenhuizen",
              sources: getSource("1873SMGA"),
            },
          },
          {
            name: "Willem Egbert Huijgens",
            birth: {
              date: "06-03-1873",
              city: "Rotterdam",
              sources: getSource("1873SRGA"),
            },
            death: {
              date: "04-03-1944",
              city: "Zeist",
              sources: getSource("1944UAOA"),
            },
            mainAncestor: true,
          },
          {
            name: "Hendrik Rutgerus Egbert Huijgens",
            birth: {
              date: "30-03-1874",
              city: "Rotterdam",
              sources: getSource("1874SRGA"),
            },
          },
          {
            name: "Ida Johanna Huijgens",
            birth: {
              date: "23-05-1875",
              city: "Rotterdam",
              sources: getSource("1875SRGA"),
            },
          },
          {
            name: "Jan Huijgens",
            birth: {
              date: "19-04-1877",
              city: "Rotterdam",
              sources: getSource("1877SRGA"),
            },
          },
          {
            name: "Adrianus Huijgens",
            birth: {
              date: "02-09-1878",
              city: "Rotterdam",
              sources: getSource("1878SRGA"),
            },
          },
          {
            name: "Elisabeth Christina Wilhelmina Alida Huijgens",
            birth: {
              date: "03-11-1879",
              city: "Rotterdam",
              sources: getSource("1879SRGA"),
            },
            death: {
              date: "09-06-1881",
              city: "Rotterdam",
              sources: getSource("1881SROA1"),
            },
          },
          {
            name: "Geertruida Hendrika Fredrika Huijgens",
            birth: {
              date: "17-01-1881",
              city: "Rotterdam",
              sources: getSource("1881SRGA"),
            },
            death: {
              date: "21-04-1881",
              city: "Rotterdam",
              sources: getSource("1881SROA2"),
            },
          },
          {
            name: "Jacob Andries Huijgens",
            birth: {
              date: "15-02-1882",
              city: "Rotterdam",
              sources: getSource("1882SRGA"),
            },
          },
          {
            name: "Christiaan Huijgens",
            birth: {
              date: "10-05-1883",
              city: "Rotterdam",
              sources: getSource("1883SRGA"),
            },
          },
        ],
      },
    ],
  });

  // Generation 2
  parents.push({
    name: "Willem Egbert Huijgens",
    backgroundInfo: {
      text: "Willem was winkelbediende ten tijde van de geboorte van zijn eerste kind. Later was hij pakhuisknecht en nog later drogist. In 1901 woonde hij aan de Oostmaasstraat in Rotterdam.",
    },
    birth: {
      date: "06-03-1873",
      city: "Rotterdam",
      sources: getSource("1873SRGA"),
    },
    death: {
      date: "04-03-1944",
      city: "Zeist",
      sources: getSource("1944UAOA"),
    },

    mariages: [
      {
        date: "06-05-1896",
        city: "Rotterdam",
        sources: getSource("1896SRHA"),
        partner: {
          name: "Johanna Hendrik Carlos",
          birth: {
            date: "01-01-1872",
            city: "Rotterdam",
          },
        },
        children: [
          {
            name: "Hendrik Rutgerus Egbert Huijgens",
            birth: {
              date: "08-09-1896",
              city: "Rotterdam",
              sources: getSource("1896SRGA"),
            },
          },
          {
            name: "Hendrika Willemina Huijgens",
            birth: {
              date: "07-06-1898",
              city: "Rotterdam",
              sources: getSource("1898SRGA"),
            },
          },
          {
            name: "Christina Huijgens",
            birth: {
              date: "25-09-1899",
              city: "Rotterdam (Honingerdijk 8316)",
              sources: getSource("1933SRGA"),
            },
            death: {
              date: "16-05-1963",
              city: "Roterdam (van Citterstraat 19a)",
              sources: getSource("1963KROK"),
            },
            burrial: {
              date: "21-05-1963",
              city: "Rotterdam (Hofwijk)",
            },
            mainAncestor: true,
          },
          {
            name: "n.n. Huijgens",
            birth: {
              date: "19-10-1901",
              city: "Rotterdam",
              sources: getSource("1901SROA"),
            },
          },
          {
            name: "Isabella Johanna Huijgens",
            birth: {
              date: "23-02-1904",
              city: "Rotterdam",
              sources: getSource("1904SRGA"),
            },
          },
          {
            name: "Willem Egbert Huijgens",
            birth: {
              date: "27-01-1906",
              city: "Rotterdam",
              sources: getSource("1906SRGA"),
            },
          },
          {
            name: "Alberdina Johanna Huijgens",
            birth: {
              date: "30-07-1912",
              city: "Rotterdam",
              sources: getSource("1912SRGA"),
            },
          },
        ],
      },
    ],
  });

  // Generation 1
  parents.push({
    name: "Christina Huygens",
    birth: {
      date: "25-09-1899",
      city: "Rotterdam (Honingerdijk 8316)",
      sources: getSource("1933SRGA"),
    },
    death: {
      date: "16-05-1963",
      city: "Roterdam (van Citterstraat 19a)",
      sources: getSource("1963KROK"),
    },
    burrial: {
      date: "21-05-1963",
      city: "Rotterdam (Hofwijk)",
    },
  });

  return parents;
};
